import React, {
	createContext,
	useContext,
	useCallback,
	useEffect,
	useMemo,
	useReducer,
} from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useAtomValue, useSetAtom } from "jotai";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

import { selectedClassAtom, unsavedChangesAtom } from "../components/Atoms/atoms";

import { getClassIdsByDeptIdAndTerm } from "../components/Api/Getters";
import { updateClassAdjust } from "../components/Api/Updates";

import { roles, rolesDescription } from "../components/Utils/Descriptions";
import {
	buildAssignmentContext,
	suggestRow,
} from "../pages/SPOTSurveys/SPOTOrdering/components/blockAssignment";

dayjs.extend(isBetween);

// ────────────────────────────────────────────────────────────────────────────────
// TYPES / CONSTANTS
// ────────────────────────────────────────────────────────────────────────────────

const ACTIONS = {
	SET_ROWS: "SET_ROWS",
	UPDATE_ROW: "UPDATE_ROW",
	UPDATE_ROWS_BATCH: "UPDATE_ROWS_BATCH",
	RESET_TO_ORIGINAL: "RESET_TO_ORIGINAL",
	APPLY_FILTER: "APPLY_FILTER",
	CLEAR_FILTER: "CLEAR_FILTER",
};

const SEARCH_STORAGE_KEY_PREFIX = "spotOrderingSearch";

// ────────────────────────────────────────────────────────────────────────────────
// HELPERS
// ────────────────────────────────────────────────────────────────────────────────

/**
 * Transform raw API classId data into the shape we need for display.
 * This includes: extracting blue_course_entry, enriching roles, calculating derived fields.
 */
function transformClassIdData(classId) {
	// Merge blue_course_entry data if present
	let row;
	if (classId.blue_course_entry) {
		const { blue_course_entry, ...rest } = classId;
		row = { ...blue_course_entry, classid_entry: rest };
	} else {
		row = { ...classId };
	}
	row._hasBlueCourseEntry = Boolean(classId.blue_course_entry);

	// Normalize and add ID for DataGrid
	row.id = row.classid;
	row.combined = false;
	row.comb_classes = [];

	// Enrich roles with descriptions and determine primary instructor
	if (row.roles && row.roles.length > 0) {
		row.roles = row.roles.map((role) => ({
			...role,
			checked: false,
			role_description: rolesDescription.find((r) => r.id === role.class_role)?.name ?? "",
		}));

		// Find the primary instructor (first matching priority role)
		for (const roleType of roles) {
			const foundRole = row.roles.find((r) => r.class_role === roleType);
			if (foundRole) {
				row.name = foundRole.name;
				row.class_role = foundRole.class_role;
				row.empid = foundRole.empid;
				break;
			}
		}
	} else {
		row.name = "";
		row.class_role = "";
		row.empid = "";
	}

	return row;
}

/**
 * Compute derived fields that depend on row data.
 * These are prefixed with _ to indicate they're computed.
 */
function computeDerivedFields(row) {
	const derived = { ...row };

	// Block size classification
	derived._blockSize = row.csize >= 10 ? "LARGE" : "SMALL";

	// Instructor flags
	if (row.roles?.length > 0) {
		derived._hasNonTARole = row.roles.some((role) => role.class_role !== "TA");
		derived._hasInstructors = true;
	} else {
		derived._hasNonTARole = false;
		derived._hasInstructors = false;
	}

	// Validation flags
	derived._hasNoRoles = !row.roles || row.roles.length === 0;
	derived._isClassSizeZero = row.csize === 0;
	derived._isClassSizeTooSmall = row.csize > 0 && row.csize < 4;

	return derived;
}

/**
 * Calculate default survey dates based on class end date and exam date.
 */
function isSpringSummerCourse(row, startDate, endDate) {
	const termDescription = String(row.term_desc ?? row.classid_entry?.term_desc ?? "").toLowerCase();

	if (termDescription.includes("spring") || termDescription.includes("summer")) {
		return true;
	}

	if (termDescription.includes("fall") || termDescription.includes("winter")) {
		return false;
	}

	const termNumber = Number(row.term?.term ?? row.term);
	if (Number.isFinite(termNumber)) {
		const seasonCode = Math.floor(termNumber / 10) % 4;
		return seasonCode === 3 || seasonCode === 0;
	}

	return startDate.isValid() && endDate.isValid() && endDate.diff(startDate, "day") <= 21;
}

function hasInvalidSurveyWindow(row, earliestStart, latestEnd) {
	const surveyStart = row.survey_start ? dayjs(row.survey_start).startOf("day") : null;
	const surveyEnd = row.survey_end ? dayjs(row.survey_end).startOf("day") : null;

	if (!surveyStart?.isValid() || !surveyEnd?.isValid()) {
		return true;
	}

	return (
		surveyStart.isAfter(surveyEnd, "day") ||
		surveyStart.isBefore(earliestStart, "day") ||
		(latestEnd?.isValid() && surveyEnd.isAfter(latestEnd, "day"))
	);
}

function buildDefaultSurveyWindow(row) {
	const courseStart = dayjs(row.c_sdate).startOf("day");
	const courseEnd = dayjs(row.c_edate).startOf("day");

	if (!courseEnd.isValid()) {
		return { surveyStart: null, surveyEnd: null };
	}

	const examDate = row.examdate ? dayjs(row.examdate).startOf("day") : null;
	const validExamDate = examDate?.isValid() ? examDate : null;
	const isSpringSummer = isSpringSummerCourse(row, courseStart, courseEnd);
	const leadDays = isSpringSummer ? 7 : 14;
	const trailingDays = isSpringSummer ? 1 : 2;

	let surveyStart = courseEnd.subtract(leadDays, "day");
	let surveyEnd = courseEnd.add(trailingDays, "day");

	if (validExamDate && !validExamDate.isAfter(surveyEnd, "day")) {
		surveyEnd = validExamDate.subtract(1, "day");
		surveyStart = validExamDate.subtract(leadDays + 1, "day");
	}

	if (validExamDate && !surveyEnd.isBefore(validExamDate, "day")) {
		surveyEnd = validExamDate.subtract(1, "day");
	}

	const earliestStart = dayjs().add(2, "day").startOf("day");
	if (surveyStart.isBefore(earliestStart, "day")) {
		surveyStart = earliestStart;
	}

	if (surveyStart.isAfter(surveyEnd, "day")) {
		return { surveyStart: null, surveyEnd: null };
	}

	return { surveyStart, surveyEnd };
}

function calculateSurveyDates(row) {
	if (!row.c_edate || row.evaluate === "Yes" || row.evaluate === "No") {
		return row;
	}

	const updated = { ...row };
	const earliestStart = dayjs().add(2, "day").startOf("day");
	const examDate = row.examdate ? dayjs(row.examdate).startOf("day") : null;
	const latestEnd = examDate?.isValid() ? examDate.subtract(1, "day") : null;

	const needsSurveyStart = !row.survey_start || !dayjs(row.survey_start).isValid();
	const needsSurveyEnd = !row.survey_end || !dayjs(row.survey_end).isValid();
	const needsSurveyWindow =
		needsSurveyStart || needsSurveyEnd || hasInvalidSurveyWindow(row, earliestStart, latestEnd);

	if (needsSurveyWindow) {
		const { surveyStart, surveyEnd } = buildDefaultSurveyWindow(row);
		updated.survey_start = surveyStart?.isValid() ? surveyStart.format("YYYY-MM-DD") : null;
		updated.survey_end = surveyEnd?.isValid() ? surveyEnd.format("YYYY-MM-DD") : null;
	}

	return updated;
}

/**
 * Full pipeline to prepare a row for display.
 */
function prepareRow(rawRow) {
	const transformed = transformClassIdData(rawRow);
	const withSurveyDates = calculateSurveyDates(transformed);
	const withDerived = computeDerivedFields(withSurveyDates);
	return withDerived;
}

function normalizeClassId(value) {
	if (value === null || value === undefined || value === "") {
		return null;
	}

	const normalized = Number(String(value).trim());
	return Number.isFinite(normalized) ? normalized : null;
}

function parseCombinedClassIds(combineValue) {
	if (combineValue === null || combineValue === undefined || combineValue === "") {
		return [];
	}

	return String(combineValue)
		.split(",")
		.map((value) => normalizeClassId(value))
		.filter((value) => value !== null);
}

function compareCtitles(leftTitle, rightTitle) {
	const parseCtitle = (title) => {
		const normalizedTitle = String(title || "");
		const parts = normalizedTitle.split(" ");
		if (parts.length === 5) {
			return [parts[0] + " " + parts[1], parts[2], parts[3], parts[4]];
		}

		return parts;
	};

	const leftParts = parseCtitle(leftTitle);
	const rightParts = parseCtitle(rightTitle);
	const maxLength = Math.max(leftParts.length, rightParts.length);

	for (let index = 0; index < maxLength; index += 1) {
		const leftPart = leftParts[index] ?? "";
		const rightPart = rightParts[index] ?? "";

		if (leftPart < rightPart) {
			return -1;
		}

		if (leftPart > rightPart) {
			return 1;
		}
	}

	return 0;
}

function buildGroupedRows(rows) {
	if (!rows?.length) {
		return [];
	}

	const rowsById = new Map(rows.map((row) => [row.classid, row]));
	const originalOrderById = new Map(rows.map((row, index) => [row.classid, index]));
	const combinedParentById = new Map();

	rows.forEach((row) => {
		parseCombinedClassIds(row.combine).forEach((childClassId) => {
			if (
				childClassId !== row.classid &&
				rowsById.has(childClassId) &&
				!combinedParentById.has(childClassId)
			) {
				combinedParentById.set(childClassId, row.classid);
			}
		});
	});

	const getImmediateParentId = (row) => {
		const originalClassId = normalizeClassId(row.original);
		if (originalClassId !== null && rowsById.has(originalClassId)) {
			return originalClassId;
		}

		return combinedParentById.get(row.classid) ?? null;
	};

	const resolveGroupRootId = (row) => {
		let currentClassId = row.classid;
		const visitedClassIds = new Set();

		while (!visitedClassIds.has(currentClassId)) {
			visitedClassIds.add(currentClassId);
			const currentRow = rowsById.get(currentClassId);

			if (!currentRow) {
				break;
			}

			const originalClassId = normalizeClassId(currentRow.original);
			if (originalClassId !== null && rowsById.has(originalClassId)) {
				currentClassId = originalClassId;
				continue;
			}

			const combinedParentId = combinedParentById.get(currentClassId);
			if (combinedParentId !== undefined && rowsById.has(combinedParentId)) {
				currentClassId = combinedParentId;
				continue;
			}

			break;
		}

		return currentClassId;
	};

	const metadataById = new Map(
		rows.map((row) => {
			const groupRootId = resolveGroupRootId(row);
			const groupParentId = row.classid === groupRootId ? null : getImmediateParentId(row);
			return [
				row.classid,
				{
					groupRootId,
					groupParentId,
					isInstance: normalizeClassId(row.original) !== null,
				},
			];
		}),
	);

	const childrenByParentId = new Map();
	metadataById.forEach((meta, classId) => {
		if (meta.groupParentId === null) {
			return;
		}

		if (!childrenByParentId.has(meta.groupParentId)) {
			childrenByParentId.set(meta.groupParentId, []);
		}

		childrenByParentId.get(meta.groupParentId).push(classId);
	});

	const compareSiblingIds = (leftClassId, rightClassId) => {
		const leftMeta = metadataById.get(leftClassId);
		const rightMeta = metadataById.get(rightClassId);

		if (leftMeta?.isInstance !== rightMeta?.isInstance) {
			return leftMeta?.isInstance ? 1 : -1;
		}

		return leftClassId - rightClassId;
	};

	const sortedRootClassIds = rows
		.filter((row) => metadataById.get(row.classid)?.groupParentId === null)
		.sort((leftRow, rightRow) => {
			const titleDifference = compareCtitles(leftRow.ctitle, rightRow.ctitle);
			if (titleDifference !== 0) {
				return titleDifference;
			}

			return (
				(originalOrderById.get(leftRow.classid) ?? 0) -
				(originalOrderById.get(rightRow.classid) ?? 0)
			);
		})
		.map((row) => row.classid);

	const orderedClassIds = [];
	const groupStatsByRootId = new Map();

	const visitClass = (classId, depth, rootClassId) => {
		orderedClassIds.push(classId);

		const currentStats = groupStatsByRootId.get(rootClassId) ?? {
			totalCount: 0,
			instanceCount: 0,
			combinedCount: 0,
		};

		currentStats.totalCount += 1;
		if (classId !== rootClassId) {
			const meta = metadataById.get(classId);
			if (meta?.isInstance) {
				currentStats.instanceCount += 1;
			} else {
				currentStats.combinedCount += 1;
			}
		}
		groupStatsByRootId.set(rootClassId, currentStats);

		const childClassIds = [...(childrenByParentId.get(classId) ?? [])].sort(compareSiblingIds);
		childClassIds.forEach((childClassId) => {
			visitClass(childClassId, depth + 1, rootClassId);
		});
	};

	sortedRootClassIds.forEach((rootClassId) => {
		visitClass(rootClassId, 0, rootClassId);
	});

	const groupOrderById = new Map();
	const groupDepthById = new Map();
	const groupPositionByRootId = new Map();

	const trackGroupPosition = (classId, depth, rootClassId) => {
		const nextPosition = groupPositionByRootId.get(rootClassId) ?? 0;
		groupOrderById.set(classId, nextPosition);
		groupDepthById.set(classId, depth);
		groupPositionByRootId.set(rootClassId, nextPosition + 1);

		const childClassIds = [...(childrenByParentId.get(classId) ?? [])].sort(compareSiblingIds);
		childClassIds.forEach((childClassId) => {
			trackGroupPosition(childClassId, depth + 1, rootClassId);
		});
	};

	sortedRootClassIds.forEach((rootClassId) => {
		trackGroupPosition(rootClassId, 0, rootClassId);
	});

	return orderedClassIds
		.map((classId) => {
			const row = rowsById.get(classId);
			const metadata = metadataById.get(classId);
			const stats = groupStatsByRootId.get(metadata.groupRootId) ?? {
				totalCount: 1,
				instanceCount: 0,
				combinedCount: 0,
			};

			return {
				...row,
				_groupRootId: metadata.groupRootId,
				_groupParentId: metadata.groupParentId,
				_groupDepth: groupDepthById.get(classId) ?? 0,
				_groupOrder: groupOrderById.get(classId) ?? 0,
				_groupChildType:
					classId === metadata.groupRootId ? null : metadata.isInstance ? "instance" : "combined",
				_isGroupRoot: classId === metadata.groupRootId,
				_isGroupedChild: classId !== metadata.groupRootId,
				_hasGroupedChildren: stats.totalCount > 1,
				_groupChildCount: Math.max(stats.totalCount - 1, 0),
				_groupInstanceCount: stats.instanceCount,
				_groupCombinedCount: stats.combinedCount,
			};
		})
		.filter(Boolean);
}

function matchesSearchTerm(row, searchTerm) {
	const lowerTerm = searchTerm.toLowerCase();

	return (
		row.classid?.toString().includes(searchTerm) ||
		row.ctitle?.toLowerCase().includes(lowerTerm) ||
		row.roles?.some((role) => {
			const name = role.name ?? `${role.user?.last_name}, ${role.user?.first_name}`;
			return name?.toLowerCase().includes(lowerTerm);
		})
	);
}

// ────────────────────────────────────────────────────────────────────────────────
// REDUCER
// ────────────────────────────────────────────────────────────────────────────────

function rowsReducer(state, action) {
	switch (action.type) {
		case ACTIONS.SET_ROWS: {
			return {
				...state,
				rows: action.payload,
				originalRows: action.isInitial ? action.payload : state.originalRows,
			};
		}

		case ACTIONS.UPDATE_ROW: {
			const { classid, updates } = action.payload;
			const updatedRows = state.rows.map((row) =>
				row.classid === classid ? { ...row, ...updates } : row,
			);
			return { ...state, rows: updatedRows };
		}

		case ACTIONS.UPDATE_ROWS_BATCH: {
			const updateMap = new Map(action.payload.map((u) => [u.classid, u.updates]));
			//sort rows by evaluate status (Yes, Caution, No, then blank), then by classid before returning updated rows
			const updatedRows = state.rows.map((row) => {
				const updates = updateMap.get(row.classid);
				return updates ? { ...row, ...updates } : row;
			});

			updatedRows.sort((a, b) => {
				const evaluateOrder = { Yes: 1, Caution: 2, No: 3, "": 4 };
				if (evaluateOrder[a.evaluate] !== evaluateOrder[b.evaluate]) {
					return evaluateOrder[a.evaluate] - evaluateOrder[b.evaluate];
				}
				return a.ctitle.localeCompare(b.ctitle);
			});
			
			return { ...state, rows: updatedRows };
		}

		case ACTIONS.RESET_TO_ORIGINAL: {
			return {
				...state,
				rows: state.originalRows,
			};
		}

		case ACTIONS.APPLY_FILTER: {
			return { ...state, searchTerm: action.payload.searchTerm };
		}

		case ACTIONS.CLEAR_FILTER: {
			return { ...state, searchTerm: "" };
		}

		default:
			return state;
	}
}

const initialState = {
	rows: [],
	originalRows: [],
	searchTerm: "",
};

function getSearchStorageKey(deptId, termNumber) {
	if (!deptId || !termNumber) {
		return null;
	}

	return `${SEARCH_STORAGE_KEY_PREFIX}:${deptId}:${termNumber}`;
}

// ────────────────────────────────────────────────────────────────────────────────
// CONTEXT
// ────────────────────────────────────────────────────────────────────────────────

const SPOTOrderingContext = createContext(null);

export function SPOTOrderingProvider({ children, token, deptId, termNumber, blockIdsByDept }) {
	const queryClient = useQueryClient();
	const [state, dispatch] = useReducer(rowsReducer, initialState);
	const searchStorageKey = useMemo(
		() => getSearchStorageKey(deptId, termNumber),
		[deptId, termNumber],
	);

	const unsavedChanges = useAtomValue(unsavedChangesAtom);
	const setSelectedClass = useSetAtom(selectedClassAtom);
	const setUnsavedChanges = useSetAtom(unsavedChangesAtom);

	useEffect(() => {
		if (!searchStorageKey) {
			dispatch({ type: ACTIONS.CLEAR_FILTER });
			return;
		}

		const savedSearchTerm = localStorage.getItem(searchStorageKey) ?? "";
		if (savedSearchTerm) {
			dispatch({ type: ACTIONS.APPLY_FILTER, payload: { searchTerm: savedSearchTerm } });
		} else {
			dispatch({ type: ACTIONS.CLEAR_FILTER });
		}
	}, [searchStorageKey]);

	useEffect(() => {
		if (!searchStorageKey) {
			return;
		}

		if (state.searchTerm) {
			localStorage.setItem(searchStorageKey, state.searchTerm);
		} else {
			localStorage.removeItem(searchStorageKey);
		}
	}, [searchStorageKey, state.searchTerm]);

	// ─── DATA FETCHING ───────────────────────────────────────────────────────────

	const { isFetching: isLoading } = useQuery(
		["classIdObjectsByDeptAndTerm", deptId, termNumber],
		async () => {
			const classIds = await getClassIdsByDeptIdAndTerm(token, deptId, termNumber);

			return classIds.map(prepareRow).filter((row) => row.csize > 0);
		},
		{
			enabled: !!token && !!deptId && !!termNumber && !unsavedChanges,
			refetchOnWindowFocus: false,
			onSuccess: (data) => {
				dispatch({ type: ACTIONS.SET_ROWS, payload: data, isInitial: true });
			},
		},
	);

	// ─── MUTATIONS ───────────────────────────────────────────────────────────────

	const updateClassMutation = useMutation(
		async ({ classData, ccid }) => {
			console.log(classData);
			await updateClassAdjust({ token, selectedClass: classData, ccid });
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["classIdObjectsByDeptAndTerm", deptId, termNumber]);
				queryClient.invalidateQueries(["blueCoursesObjectsByDeptAndTerm", deptId, termNumber]);
				setUnsavedChanges(false);
			},
		},
	);

	// ─── COMPUTED VALUES ─────────────────────────────────────────────────────────

	// Apply block suggestions to rows
	const rowsWithSuggestions = useMemo(() => {
		if (!state.rows.length || !blockIdsByDept?.length) return state.rows;

		const ctx = buildAssignmentContext(state.rows, blockIdsByDept);
		return state.rows.map((row) => suggestRow(row, ctx));
	}, [state.rows, blockIdsByDept]);

	// The "effective" rows - source rows with computed suggestions applied.
	const effectiveRows = useMemo(() => buildGroupedRows(rowsWithSuggestions), [rowsWithSuggestions]);

	// The "display" rows - effective rows filtered by the active search term.
	const displayRows = useMemo(() => {
		if (!state.searchTerm) {
			return effectiveRows;
		}

		return effectiveRows.filter((row) => matchesSearchTerm(row, state.searchTerm));
	}, [effectiveRows, state.searchTerm]);

	// Check if there are unsaved changes
	const hasChanges = useMemo(() => {
		if (state.rows.length !== state.originalRows.length) return true;
		return state.rows.some((row, i) => {
			const original = state.originalRows.find((o) => o.classid === row.classid);
			if (!original) return true;
			// Compare key editable fields
			return (
				row.form !== original.form ||
				row.evaluate !== original.evaluate ||
				row.survey_start !== original.survey_start ||
				row.survey_end !== original.survey_end ||
				row.examdate !== original.examdate ||
				row.c_edate !== original.c_edate ||
				JSON.stringify(row.roles) !== JSON.stringify(original.roles)
			);
		});
	}, [state.rows, state.originalRows]);

	// ─── ACTIONS ─────────────────────────────────────────────────────────────────

	const updateRow = useCallback((classid, updates) => {
		dispatch({ type: ACTIONS.UPDATE_ROW, payload: { classid, updates } });
	}, []);

	const updateRowsBatch = useCallback((updates) => {
		// updates: Array<{ classid, updates: {} }>
		dispatch({ type: ACTIONS.UPDATE_ROWS_BATCH, payload: updates });
	}, []);

	const resetToOriginal = useCallback(() => {
		dispatch({ type: ACTIONS.RESET_TO_ORIGINAL });
		setUnsavedChanges(false);
	}, [setUnsavedChanges]);

	const applySearch = useCallback((searchTerm) => {
		if (!searchTerm) {
			dispatch({ type: ACTIONS.CLEAR_FILTER });
		} else {
			dispatch({ type: ACTIONS.APPLY_FILTER, payload: { searchTerm } });
		}
	}, []);

	const refreshData = useCallback(() => {
		queryClient.invalidateQueries(["classIdObjectsByDeptAndTerm", deptId, termNumber]);
		queryClient.invalidateQueries(["blueCoursesObjectsByDeptAndTerm", deptId, termNumber]);
		setSelectedClass(null);
	}, [queryClient, deptId, termNumber, setSelectedClass]);

	// ─── CONTEXT VALUE ───────────────────────────────────────────────────────────

	const value = useMemo(
		() => ({
			// State
			rows: state.rows,
			effectiveRows,
			displayRows,
			originalRows: state.originalRows,
			isLoading,
			hasChanges,
			searchTerm: state.searchTerm,

			// Actions
			updateRow,
			updateRowsBatch,
			resetToOriginal,
			applySearch,
			refreshData,

			// Mutations
			updateClassMutation,

			// For backwards compatibility during migration
			setRows: (newRows) => dispatch({ type: ACTIONS.SET_ROWS, payload: newRows }),
		}),
		[
			state.rows,
			state.originalRows,
			state.searchTerm,
			effectiveRows,
			displayRows,
			isLoading,
			hasChanges,
			updateRow,
			updateRowsBatch,
			resetToOriginal,
			applySearch,
			refreshData,
			updateClassMutation,
		],
	);

	return <SPOTOrderingContext.Provider value={value}>{children}</SPOTOrderingContext.Provider>;
}

// ────────────────────────────────────────────────────────────────────────────────
// HOOK
// ────────────────────────────────────────────────────────────────────────────────

export function useSPOTOrdering() {
	const context = useContext(SPOTOrderingContext);
	if (!context) {
		throw new Error("useSPOTOrdering must be used within SPOTOrderingProvider");
	}
	return context;
}

export default SPOTOrderingContext;
